<script lang="ts">
  /**
   * T28 — ReportActions.svelte
   * Botões de ação para download HTML, impressão/PDF e toggle de preview.
   */
  import type { ReportModelT27 } from '$core/report/report.types';
  import {
    exportReportAsHtml,
    downloadBlob,
    printReportToPdf,
    buildSafeReportFileName,
  } from '$core/report/htmlExport';
  import Button from '../../components/Button.svelte';
  import PrintableReport from './PrintableReport.svelte';

  export let report: ReportModelT27;

  let showPreview = false;
  let previewKey = 0;

  function handleDownloadHtml(): void {
    const blob = exportReportAsHtml(report);
    const fileName = buildSafeReportFileName(report);
    downloadBlob(blob, fileName);
  }

  function handlePrint(): void {
    // Garante que o PrintableReport está montado antes de imprimir
    if (!showPreview) {
      showPreview = true;
      previewKey += 1;
      // Aguarda a renderização antes de imprimir
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          printReportToPdf();
        });
      });
    } else {
      printReportToPdf();
    }
  }

  function handleTogglePreview(): void {
    showPreview = !showPreview;
    if (showPreview) {
      previewKey += 1;
    }
  }
</script>

<div class="report-actions">
  <div class="report-actions__buttons">
    <Button variant="primary" on:click={handleDownloadHtml}>
      Baixar HTML
    </Button>

    <Button variant="secondary" on:click={handlePrint}>
      Imprimir / PDF
    </Button>

    <Button variant="ghost" on:click={handleTogglePreview}>
      {showPreview ? 'Ocultar' : 'Visualizar'}
    </Button>
  </div>

  <p class="report-actions__hint">
    Salve como PDF usando a opção "Salvar como PDF" na janela de impressão do navegador.
  </p>
</div>

{#if showPreview}
  <div class="report-actions__preview" aria-label="Pré-visualização do relatório">
    {#key previewKey}
      <PrintableReport {report} />
    {/key}
  </div>
{/if}

<style>
  .report-actions {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-3, 0.75rem);
    padding: var(--ptt-space-4, 1rem);
    background: var(--ptt-gray-10, #f5f5f5);
    border-radius: var(--ptt-radius-md, 6px);
    border: 1px solid var(--ptt-gray-20, #e7e7e7);
  }

  .report-actions__buttons {
    display: flex;
    gap: var(--ptt-space-3, 0.75rem);
    flex-wrap: wrap;
    align-items: center;
  }

  .report-actions__hint {
    font-size: 0.8rem;
    color: var(--ptt-gray-100, #7d7d7d);
    margin: 0;
  }

  .report-actions__preview {
    margin-top: var(--ptt-space-5, 1.25rem);
    border: 1px solid var(--ptt-gray-20, #e7e7e7);
    border-radius: var(--ptt-radius-md, 6px);
    padding: var(--ptt-space-5, 1.25rem);
    background: var(--ptt-white, #ffffff);
  }

  @media print {
    .report-actions {
      display: none;
    }

    .report-actions__preview {
      border: none;
      padding: 0;
      margin: 0;
    }
  }
</style>
